import { createContext, useContext, useState, useEffect } from 'react';
import defaultBranding from '../config/branding.json';

const BrandingContext = createContext(null);

export function BrandingProvider({ children, customBranding = null }) {
  const [branding, setBranding] = useState(customBranding || defaultBranding);
  const [loading, setLoading] = useState(true);

  // Fetch branding from API based on subdomain
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        // Get subdomain from hostname
        const hostname = window.location.hostname;
        const subdomain = hostname.split('.')[0];
        
        // Skip API call for localhost or main domain
        if (hostname === 'localhost' || subdomain === 'www' || subdomain === hostname) {
          setLoading(false);
          return;
        }
        
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${apiUrl}/api/branding?subdomain=${subdomain}`);
        
        if (response.ok) {
          const apiBranding = await response.json();
          if (apiBranding && apiBranding.company) {
            setBranding(prev => ({
              ...prev,
              ...apiBranding,
              theme: {
                ...prev.theme,
                ...apiBranding?.theme,
                colors: {
                  ...prev.theme.colors,
                  ...apiBranding?.theme?.colors
                }
              }
            }));
          }
        }
      } catch (error) {
        console.log('Using default branding');
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  // Apply CSS variables from branding config
  useEffect(() => {
    const root = document.documentElement;
    const colors = branding.theme.colors;
    
    // Set CSS custom properties
    Object.entries(colors).forEach(([key, value]) => {
      const cssVar = `--brand-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVar, value);
    });
    
    // Set fonts
    if (branding.theme.fonts) {
      root.style.setProperty('--font-heading', branding.theme.fonts.heading);
      root.style.setProperty('--font-body', branding.theme.fonts.body);
    }
    
    // Set border radius
    if (branding.theme.borderRadius) {
      Object.entries(branding.theme.borderRadius).forEach(([key, value]) => {
        root.style.setProperty(`--radius-${key}`, value);
      });
    }

    // Update document title
    document.title = branding.company.name;
    
    // Update favicon if provided
    if (branding.company.favicon) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = branding.company.favicon;
      }
    }
  }, [branding]);

  // Function to update branding dynamically
  const updateBranding = (newBranding) => {
    setBranding(prev => ({
      ...prev,
      ...newBranding,
      theme: {
        ...prev.theme,
        ...newBranding?.theme,
        colors: {
          ...prev.theme.colors,
          ...newBranding?.theme?.colors
        }
      }
    }));
  };

  // Get product config
  const getProductConfig = (productId) => {
    return branding.products[productId] || null;
  };

  // Check if product is enabled
  const isProductEnabled = (productId) => {
    return branding.products[productId]?.enabled ?? false;
  };

  const value = {
    branding,
    company: branding.company,
    theme: branding.theme,
    products: branding.products,
    features: branding.features,
    footer: branding.footer,
    updateBranding,
    getProductConfig,
    isProductEnabled,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  
  if (!context) {
    // Return default branding if not in provider
    return {
      branding: defaultBranding,
      company: defaultBranding.company,
      theme: defaultBranding.theme,
      products: defaultBranding.products,
      features: defaultBranding.features,
      footer: defaultBranding.footer,
      updateBranding: () => {},
      getProductConfig: (id) => defaultBranding.products[id],
      isProductEnabled: (id) => defaultBranding.products[id]?.enabled ?? false,
    };
  }
  
  return context;
}

export default BrandingContext;
